import { HospitalityRoomStatus } from '../constants/roomStatuses'
import type { HotelsTranslationValues } from '../i18n'

type HotelsTextFn = (
  key: string,
  fallback: string,
  values?: HotelsTranslationValues,
) => string

export const getHotelsNotAvailableText = (text: HotelsTextFn): string => {
  return text('hotels.common.notAvailable', 'N/A')
}

export const getHotelsPlaceholderText = (text: HotelsTextFn): string => {
  return text('hotels.common.placeholder', '—')
}

export const valueOrPlaceholder = (
  value: string | number | null | undefined,
  text: HotelsTextFn,
): string | number => {
  if (value === null || value === undefined || value === '') {
    return getHotelsPlaceholderText(text)
  }

  return value
}

const getRoomStatusLabel = (
  statusCode: number,
  text: HotelsTextFn,
): string | null => {
  switch (statusCode) {
    case HospitalityRoomStatus.DIRTY:
      return text('hotels.dashboard.roomStatusActions.dirty', 'Dirty')
    case HospitalityRoomStatus.CLEAN:
      return text('hotels.dashboard.roomStatusActions.clean', 'Clean')
    case HospitalityRoomStatus.OCCUPIED:
      return text('hotels.dashboard.roomStatusActions.occupied', 'Occupied')
    case HospitalityRoomStatus.INSPECTED:
      return text('hotels.dashboard.roomStatusActions.inspected', 'Inspected')
    default:
      return null
  }
}

/**
 * Parse a legacy hospitality_status timestamp string into a Date.
 *
 * The backend writes `timezone.now().strftime("%Y-%m-%d %H:%M:%S")` which
 * produces non-ISO `"YYYY-MM-DD HH:MM:SS"` strings in UTC with no zone
 * marker. V8/Chrome parses that format as **local time**, so feeding it
 * straight into `new Date(...)` shifts the rendered timestamp by the
 * user's UTC offset (e.g. a status set at 21:29 UTC renders as 9:29 PM
 * for a UTC-5 viewer instead of the correct 4:29 PM).
 *
 * Normalize to a real ISO 8601 string with explicit `Z` so parsing is
 * deterministic across engines. Already-ISO inputs (with `T` and/or a
 * `Z`/offset) are left alone.
 */
const parseHospitalityTimestamp = (raw: string): Date | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const hasIsoSeparator = trimmed.includes('T')
  // Anchor both alternatives at the end so we only treat genuine
  // trailing UTC markers (`Z`) or numeric offsets (`+0000`, `-05:00`)
  // as already-zoned — otherwise an unrelated `Z` mid-string could
  // suppress normalization.
  const hasTimezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)

  let normalized = trimmed
  if (!hasIsoSeparator) {
    normalized = normalized.replace(' ', 'T')
  }
  if (!hasTimezone) {
    normalized = `${normalized}Z`
  }

  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Parse legacy hospitality_status format: "{status_code}_{timestamp}"
 * and return a localized string like "Dirty (4:29 PM - 4/28)".
 */
export const formatHospitalityStatus = (
  hospitalityStatus: string | null | undefined,
  locale: string,
  text: HotelsTextFn,
): string => {
  const notAvailable = getHotelsNotAvailableText(text)

  if (!hospitalityStatus) {
    return notAvailable
  }

  const separatorIndex = hospitalityStatus.indexOf('_')
  if (separatorIndex < 0) {
    return hospitalityStatus
  }

  const codeRaw = hospitalityStatus.slice(0, separatorIndex)
  const timestampRaw = hospitalityStatus.slice(separatorIndex + 1)

  // `Number("")` is `0`, which would silently coerce a malformed
  // `"_<timestamp>"` payload into DIRTY. Treat empty/whitespace-only
  // codes as missing so they fall back to N/A.
  const codeIsPresent = codeRaw.trim() !== ''
  const statusCode = codeIsPresent ? Number(codeRaw) : Number.NaN
  const statusLabel =
    codeIsPresent && Number.isFinite(statusCode)
      ? (getRoomStatusLabel(statusCode, text) ?? notAvailable)
      : notAvailable

  const date = parseHospitalityTimestamp(timestampRaw)
  if (!date) {
    return statusLabel
  }

  const time = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)

  const shortDate = new Intl.DateTimeFormat(locale, {
    month: 'numeric',
    day: '2-digit',
  }).format(date)

  return text(
    'hotels.settings.statusAtTime',
    '{{status}} ({{time}} - {{date}})',
    {
      status: statusLabel,
      time,
      date: shortDate,
    },
  )
}
